import { ArrowSmRightIcon } from '@heroicons/react/outline';
import { Affix } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import WhiteKeyIcon from 'assets/Icons/WhiteKeyIcon';
import clsx from 'clsx';
import { AnimatePresence } from 'framer-motion';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import { checkIsPurchased } from '@/services/ReviewLandingPage';
import { hasPreviouslySubscribed, isSubscriptionActive } from '@/utils/index';

import { authModalControl, tokenUpdate } from '../../../atoms/authModalControl';
import { localUserID, user } from '../../../atoms/user';
import AuthorDetailsSection from '../../../components/BookLandingPageComp/AuthorDeatils';
import BookDescription from '../../../components/BookLandingPageComp/BookDescription';
import CurriculumSection from '../../../components/BookLandingPageComp/CurriculumSection';
import HeroSection from '../../../components/BookLandingPageComp/HeroSection';
import IframeLandingPage from '../../../components/BookLandingPageComp/IframeLandingPage';
import RecommendedAndOverViewSection from '../../../components/BookLandingPageComp/RecommendedAndOverViewSection';
import RecommendedBooksSection from '../../../components/BookLandingPageComp/RecommendedBooksSection';
import ReviewsLandingPage from '../../../components/BookLandingPageComp/ReviewsLandingPage';
import SlidBookContainer from '../../../components/BookLandingPageComp/SlidBookContainer';
import SneakPeekSection from '../../../components/BookLandingPageComp/SneakPeekSection';
import Products from '../../../components/ProjectPage/Products';
import ButtonDefault, {
  ButtonVariants,
} from '../../../components/shared/basic/button';
import Section from '../../../components/shared/basic/section';
import { ShouldRender } from '../../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../../components/shared/formateErrorObject';
import { getLoginToken } from '../../../components/shared/getToken';
import { trackAddToCartEvent } from '../../../components/shared/Gtags';
import {
  extractAuthorDetailsData,
  extractCurriculumLandingPageData,
  extractHeroSectionData,
  extractIBookDescriptionData,
  extractIframeData,
  extractRecommendedAndOverViewSectionData,
  extractSlidBookContainerData,
  extractSneakPeekData,
} from '../../../components/shared/LandingPageHelper';
import CustomLoader from '../../../components/shared/Loading';
import { notify } from '../../../components/shared/notify';
import { Endpoints } from '../../../network';
import {
  addCouponCode,
  addToCart,
  addToRecentlyViewed,
} from '../../../services/Cart';
import {
  getBookLandingPageData,
  getBookLandingPageDataSSR,
  getLandingPageSeoSSR,
} from '../../../services/LandingPage';
import { getBookBySlug } from '../../../services/Library/Books';
import { getAuthorProfileById } from '../../../services/School';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import {
  IAuthorDetails,
  IAuthorDetailsList,
  IBookDescription,
  IBookLandingPage,
  ICurriculumLandingPageData,
  IHeroSectionLandingPage,
  IIframeLandingPage,
  IRecommendedAndOverViewSection,
  ISlidBook,
  ISneakPeek,
} from '../../../utils/interfaces/landingPage';
import { IBookDetails } from '../../../utils/interfaces/Products/books';
import { User } from '../../../utils/interfaces/User';

interface Props {
  metaData: {
    title: string;
    description: string;
    author: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
    categories?: [
      {
        _id: string;
        name: string;
      },
    ];
    slug: string;
    price: number;
    reviews: any[];
    ratingValue: number;
    reviewCount: number;
  } | null;
}

export interface IAuthorInterface {
  follower_count: string;
  user_name: string;
  is_followed?: boolean;
  is_verified?: boolean;
}

export const generateUrlForImage = (
  baseUrl: string,
  endpoint: string,
  id: string,
): string => {
  const encodedId = encodeURIComponent(id);
  const url = `${baseUrl}${endpoint}${encodedId}`;
  return url;
};

const BookDetailsPage: React.FC<Props> = ({ metaData }) => {
  const [bookLandingDetails, setBookLandingDetails] =
    useState<IBookLandingPage>();
  const [loading, setLoading] = useState(false);

  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });

  const [token, setToken] = useState('');
  const [offset, setOffset] = useState<number>(0);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [correctedSlug, setCorrectedSlug] = useState<string>();
  const [authorsData, setAuthorsData] = useState<
    IAuthorInterface | IAuthorInterface[]
  >();
  const [bookDetails, setBooksDetails] = useState<IBookDetails>();
  const [, setVisible] = useRecoilState(authModalControl);
  const [lastFetchedBookId, setLastFetchedBookId] = useState<string | null>(
    null,
  );

  const [purchasedData, setPurchasedData] = useState<{
    hardProduct: {
      purchased: boolean;
    };
    softProduct: {
      purchased: boolean;
    };
  }>({
    hardProduct: {
      purchased: false,
    },
    softProduct: {
      purchased: false,
    },
  });

  const router = useRouter();
  const screens = useBreakpoint();
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);

  const { bookId, swp, short } = router.query;

  const claimHandle = (productId: number) => {
    router.push(`/subscription-checkout?productId=${productId}`);
  };

  const checkIsPurchasedHandle = async () => {
    try {
      if (!bookLandingDetails) return;

      const hard = await checkIsPurchased(
        bookLandingDetails?.hardCoverProductId?.toString(),
      );
      setPurchasedData((prevState) => ({
        ...prevState,
        hardProduct: {
          purchased: hard,
        },
      }));
      const soft = await checkIsPurchased(
        bookLandingDetails?.digitalProductId.toString(),
      );
      setPurchasedData((prevState) => ({
        ...prevState,
        softProduct: {
          purchased: soft,
        },
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const getBookDetailsById = async (bookId: string) => {
    try {
      setLoading(true);
      const response = await getBookLandingPageData(bookId);

      if (response?.data?.correctedSlug) {
        setCorrectedSlug(response?.data?.correctedSlug);
      }

      setBookLandingDetails({
        ...response?.data?.form,
        bookType: response?.data?.bookType,
        categories: response?.data?.categories?.map((category: any) => ({
          categoryId: category._id,
          name: category.name,
        })),
      });

      if (response?.data?.bookType === 'short' && short !== 'true') {
        router.push(`${router.asPath.split('?')[0]}?short=true`, undefined, {
          shallow: true,
        });
      }
    } catch (error: any) {
      console.error('API call error:', error);

      if (error?.response?.status === 404) {
        router.push('/books');
      }
    } finally {
      setLoading(false);
    }
  };

  const getBookDetailsByIdSwp = async (bookId: string, userId?: string) => {
    try {
      setLoading(true);

      const response = await getBookBySlug(bookId, userId && userId);

      const formateData = {
        authorName: response?.authors?.[0],
        bookName: response?.title,
        desc: response?.description,
        lang: 'English',
        type: 'Book',
        _id: response?._id,
        authorAvatar: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}`,
          `${Endpoints.getAuthorAvatar}/`,
          `${response?.author_list?.[0]?._id}`,
        ),
        totalPages: response?.totalPages,
        bookLSample: response?._id,
        bookType: response?.type,
        isSubscribed: response?.subscribe?.subscribed || false,
        image: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${response?._id}`,
        ),
      };
      setBooksDetails(formateData);
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateOffset = () => {
      const containerHeight =
        document.querySelector('.slid-book-container')?.clientHeight || 0;
      const screenHeight = window.innerHeight;
      setOffset((screenHeight - containerHeight) / 15);
    };

    updateOffset();

    window.addEventListener('resize', updateOffset);

    return () => {
      window.removeEventListener('resize', updateOffset);
    };
  }, [bookId]);

  useEffect(() => {
    if (bookId && userData) {
      if (swp === 'false') {
        getBookDetailsByIdSwp(bookId as string, userData && userData?.zl_uid);
      }
    }
  }, [userData, bookId]);

  useEffect(() => {
    if (bookId && bookId !== lastFetchedBookId) {
      if (swp === 'false') {
        getBookDetailsByIdSwp(bookId as string);
      } else {
        getBookDetailsById(bookId as string);
      }

      setLastFetchedBookId(bookId as string);
    }
  }, [bookId, userData, token, short]);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  useEffect(() => {
    const baseSlug = `/books/${bookLandingDetails?.productSlug}-${bookLandingDetails?.readersProductId}`;
    const queryParams = short === 'true' ? '?short=true' : '';
    const formattedSlug = `${baseSlug}${queryParams}`;

    if (
      correctedSlug &&
      bookLandingDetails?.readersProductId &&
      (router.pathname?.split('?')[0] || '') !== baseSlug
    ) {
      router.replace(formattedSlug, undefined, { shallow: true });
    }
  }, [correctedSlug, bookLandingDetails, short]);

  useEffect(() => {
    checkIsPurchasedHandle();
  }, [bookLandingDetails]);

  const keyCount = useMemo(() => {
    return (
      userData?.features.reduce(
        (sum, i) => sum + (i?.available_quantity || 0),
        0,
      ) || 0
    );
  }, [userData]);

  const handleApplyCoupon = async (coupon: string) => {
    try {
      await addCouponCode(coupon as string);
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    }
  };

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    type: 'hardCopy' | 'digitalCopy',
    isBuyNow?: boolean,
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      const data = await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${slidBookContainerData?.bookName} ${type === 'hardCopy' ? 'Hard Copy' : 'Digital Copy'}  added to cart successfully!`,
        'success',
      );
      if (isBuyNow) {
        await handleApplyCoupon(heroSectionData?.productCouponCode || '');
        router.push(`/checkout?cartId=${data?.data?.cart_id}&pre`);
      } else {
        router.push(`/cart/`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  const heroSectionData: IHeroSectionLandingPage | undefined =
    bookLandingDetails ? extractHeroSectionData(bookLandingDetails) : undefined;

  const slidBookContainerData: ISlidBook | undefined = bookLandingDetails
    ? extractSlidBookContainerData(bookLandingDetails)
    : undefined;

  const bookDescriptionData: IBookDescription | undefined = bookLandingDetails
    ? extractIBookDescriptionData(bookLandingDetails)
    : undefined;

  const recommendedAndOverViewSectionData:
    | IRecommendedAndOverViewSection
    | undefined = bookLandingDetails
    ? extractRecommendedAndOverViewSectionData(bookLandingDetails)
    : undefined;

  const curriculumLandingPageData: ICurriculumLandingPageData | undefined =
    bookLandingDetails
      ? extractCurriculumLandingPageData(bookLandingDetails)
      : undefined;

  const sneakPeekData: ISneakPeek | undefined = bookLandingDetails
    ? extractSneakPeekData(bookLandingDetails)
    : undefined;

  const authorDetailsData: IAuthorDetails | IAuthorDetailsList[] | undefined =
    bookLandingDetails?.isMultipleAuthors
      ? bookLandingDetails?.authorsList
      : bookLandingDetails
        ? extractAuthorDetailsData(bookLandingDetails)
        : undefined;

  const iframeData: IIframeLandingPage | undefined = bookLandingDetails
    ? extractIframeData(bookLandingDetails)
    : undefined;

  const handleGetAuthorProfileById = async (zl_uid: string) => {
    try {
      const data = await getAuthorProfileById({ zl_uid });
      return {
        user_name: data?.data?.profile_data?.user_name,
        follower_count: data?.data?.follower_count,
        is_followed: data?.data?.is_followed,
        is_verified: data?.data?.is_verified,
      };
    } catch (error: any) {
      return null;
    }
  };

  const fetchAuthorData = async () => {
    if (
      heroSectionData?.isMultipleAuthors &&
      Array.isArray(heroSectionData?.authorsList)
    ) {
      const results = await Promise.all(
        heroSectionData.authorsList.map(
          (author) =>
            author.authorZlUid &&
            handleGetAuthorProfileById(author.authorZlUid),
        ),
      );
      const filtered = results.filter(Boolean) as IAuthorInterface[];
      setAuthorsData(filtered);
    } else if (heroSectionData?.authorZlUid) {
      const singleAuthor = await handleGetAuthorProfileById(
        heroSectionData.authorZlUid,
      );
      if (singleAuthor) {
        setAuthorsData(singleAuthor);
      }
    }
  };

  useEffect(() => {
    fetchAuthorData();
  }, [heroSectionData?.isMultipleAuthors, heroSectionData?.authorZlUid]);

  // Product Schema
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: metaData?.title || 'Default Product Name',
    brand: {
      '@type': 'Brand',
      name: 'ZebraLearn',
    },
    image: metaData?.image || 'https://zebralearn.com/logo.png',
    description: metaData?.description,
    category: 'Books',
    review: metaData?.reviews?.length
      ? metaData.reviews.map((r: any) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r?.user?.name ?? 'zebralearn' },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r?.rating ?? 4,
            bestRating: 5,
          },
          reviewBody: r.text,
        }))
      : undefined,
    aggregateRating:
      metaData?.reviews?.length && metaData.reviews.length > 0
        ? {
            '@type': 'AggregateRating',
            ratingValue: (
              metaData.reviews.reduce(
                (sum: number, r: any) => sum + (r?.rating ?? 4),
                0,
              ) / metaData.reviews.length
            ).toFixed(1),
            reviewCount: metaData.reviews.length,
            bestRating: Math.max(
              ...(metaData.reviews.map((r: any) => r?.rating ?? 4) || [5]),
            ),
          }
        : undefined,
  };

  const claimSoftButton = (
    <ButtonDefault
      disabled={purchasedData?.softProduct?.purchased || keyCount === 0}
      variant={ButtonVariants.PRIMARY}
      is3D
      size={0}
      className='w-full px-3 py-[9px]'
      onClick={() =>
        bookLandingDetails && claimHandle(bookLandingDetails?.digitalProductId)
      }
    >
      <p className='text-sm leading-[22px] font-semibold text-white '>
        Claim Digital Copy with 1
      </p>
      <WhiteKeyIcon />
    </ButtonDefault>
  );

  const claimHardButton = (
    <ButtonDefault
      disabled={purchasedData?.hardProduct?.purchased || keyCount === 0}
      size={0}
      variant={ButtonVariants.PRIMARY}
      is3D
      className='w-full px-3 py-[9px] disabled:bg-black'
      onClick={() =>
        bookLandingDetails &&
        claimHandle(bookLandingDetails?.hardCoverProductId)
      }
    >
      <p className='text-sm leading-[22px] font-semibold text-white '>
        Claim Hard Copy with 1
      </p>
      <WhiteKeyIcon />
    </ButtonDefault>
  );
  const renderCTASection = () => {
    const bookType = bookLandingDetails?.bookType;

    const isShort = bookType === 'short';
    const isComingSoon = bookType === 'comingSoon';
    const isLong = !isShort && !isComingSoon;

    switch (true) {
      case isShort:
        return (
          <div className='flex gap-2 w-full'>
            <ButtonDefault
              size={4}
              rounded={false}
              roundedSm={true}
              onClick={() => {
                if (token) {
                  router.push(
                    `/pdf/${slidBookContainerData?.readersProductId}?samplePdf=true`,
                  );
                } else {
                  setVisible((prevState) => ({
                    ...prevState,
                    signUp: true,
                    bookId: slidBookContainerData?.readersProductId as string,
                  }));
                }
              }}
              variant={ButtonVariants.WHITE}
              className='flex w-full justify-center px-5 py-3 border-none font-medium text-xs whitespace-nowrap'
            >
              <span className='px-4 text-sm md:text-base font-semibold py-1'>
                Start Reading
              </span>
            </ButtonDefault>
          </div>
        );

      case isComingSoon:
        return (
          <div className='flex w-full justify-center'>
            <ButtonDefault
              isWidthFull={true}
              size={4}
              variant={ButtonVariants.PRIMARY}
              is3D={true}
              loading={loadingAddToCart.value}
              disabled={loadingAddToCart.value}
              onClick={(e) => {
                e.stopPropagation();
                handleAddDataToCart(
                  {
                    id: Number(heroSectionData?.hardCoverProductId) as number,
                    temp_user_id: localUserId,
                    quantity: 1,
                  },
                  'hardCopy',
                  true,
                );

                trackAddToCartEvent({
                  productData: {
                    price: Number(
                      heroSectionData?.hardCopyPrice?.discountedPrice,
                    ),
                    id: Number(heroSectionData?.hardCoverProductId),
                    heading: `${heroSectionData?.bookName} 'Hard Copy'`,
                    quantity: 1 as number,
                  },
                  userData,
                  pathname: router.pathname,
                  productType: 'hardcopy',
                });
              }}
              className='w-full p-0 py-0 px-0'
            >
              <div className='w-full flex justify-center font-semibold leading-6 items-center gap-1 py-[3px] px-3.5'>
                Pre-Order at ₹{heroSectionData?.hardCopyPrice?.comingSoonPrice}
              </div>
            </ButtonDefault>
          </div>
        );

      case isLong:
      default:
        return (
          <>
            {/* <ButtonDefault
                      size={screens.md ? 4 : 1}
                      isWidthFull={true}
                      rounded={false}
                      roundedSm={true}
                      variant={ButtonVariants.WHITE}
                      enableScaling={false}
                      loading={
                        Number(bookLandingDetails?.digitalProductId) ===
                          loadingAddToCart.id && loadingAddToCart.value
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDataToCart(
                          {
                            id: Number(
                              bookLandingDetails?.digitalProductId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                          },
                          'digitalCopy',
                        );
  
                        trackAddToCartEvent({
                          productData: {
                            price: Number(
                              bookLandingDetails?.softCopyPrice?.discountedPrice,
                            ),
                            id: Number(bookLandingDetails?.digitalProductId),
                            heading: bookLandingDetails?.productTitle as string,
                            quantity: 1 as number,
                          },
                          userData,
                          pathname: router.pathname,
                          productType: 'digitalcopy',
                        });
                      }}
                      className='w-full text-center text-sm group '
                    >
                      <div className='flex justify-between items-center w-full '>
                        <span className='flex flex-col justify-start'>
                          <p className='w-full text-start font-semibold text-[#737373] text-[10px]'>
                            Digital copy
                          </p>
                          <span className='flex gap-x-1 items-baseline pl-1'>
                            <p className='text-sm font-semibold whitespace-nowrap font-sans'>
                              {'\u20B9'}&nbsp;
                              {bookLandingDetails?.softCopyPrice?.discountedPrice}
                            </p>
                            <p className='line-through text-[#AEAEAE] font-sans text-[13px] font-semibold whitespace-nowrap'>
                              ₹ {bookLandingDetails?.softCopyPrice?.mrpPrice}
                            </p>
                          </span>
                        </span>
                        <span className=''>
                          <ArrowRightIcon className='w-4 md:w-5 text-[#AEAEAE] transform transition-transform duration-300 ease-in-out group-hover:-rotate-[20deg]' />
                        </span>
                      </div>
                    </ButtonDefault>
                    <ShouldRender
                      check={bookLandingDetails?.hardCoverAvailable !== false}
                    >
                      <ButtonDefault
                        size={screens.md ? 4 : 1}
                        rounded={false}
                        isWidthFull={true}
                        roundedSm={true}
                        loading={
                          Number(bookLandingDetails?.hardCoverProductId) ===
                            loadingAddToCart.id && loadingAddToCart.value
                        }
                        variant={ButtonVariants.PRIMARY}
                        enableScaling={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddDataToCart(
                            {
                              id: Number(
                                bookLandingDetails?.hardCoverProductId,
                              ) as number,
                              temp_user_id: localUserId,
                              quantity: 1,
                            },
                            'hardCopy',
                          );
  
                          trackAddToCartEvent({
                            productData: {
                              price: Number(
                                bookLandingDetails?.hardCopyPrice
                                  ?.discountedPrice,
                              ),
                              id: Number(bookLandingDetails?.hardCoverProductId),
                              heading: bookLandingDetails?.productTitle as string,
                              quantity: 1 as number,
                            },
                            userData,
                            pathname: router.pathname,
                            productType: 'hardcopy',
                          });
                        }}
                        className='w-full text-center text-sm   group'
                      >
                        <div className='flex justify-between items-center w-full'>
                          <span className='flex flex-col justify-start'>
                            <p className='w-full text-start font-semibold  text-[10px]'>
                              Hard copy + Digital copy
                            </p>
                            <span className='flex gap-x-2 items-baseline pl-1'>
                              <p className='text-sm font-semibold whitespace-nowrap font-sans'>
                                ₹{' '}
                                {
                                  bookLandingDetails?.hardCopyPrice
                                    ?.discountedPrice
                                }
                              </p>
                              <p className='line-through text-[#8A2800] text-[13px] font-sans font-semibold whitespace-nowrap'>
                                ₹ {bookLandingDetails?.hardCopyPrice?.mrpPrice}
                              </p>
                            </span>
                          </span>
                          <span>
                            <ArrowRightIcon className='w-4 md:w-5 transform transition-transform duration-300 ease-in-out group-hover:-rotate-[20deg] text-[#8A2800]' />
                          </span>
                        </div>
                      </ButtonDefault>
                    </ShouldRender>
                    <ShouldRender
                      check={bookLandingDetails?.hardCoverAvailable === false}
                    >
                      <Button
                        size='small'
                        type='text'
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: '#141414',
  
                          border: 'none',
                          borderRadius: '8px',
                          padding: '5px 15px',
                          zIndex: 1,
                          boxShadow: '0px 4px 4px 0px #0000004D',
                        }}
                        className='w-full h-full min-h-[62px] hover:text-white text-xs font-semibold'
                      >
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: -1,
                            borderRadius: 'inherit',
                            padding: '1.3px',
                            background:
                              'linear-gradient(180deg, #6199FF 0%, #045CFB 100%)',
                            WebkitMask:
                              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                          }}
                        />
                        <div className='flex justify-center items-center w-full '>
                          <span className='flex flex-col justify-start gap-y-1.5'>
                            <p className='w-full text-center font-semibold text-white text-[10px]'>
                              Hard Copy
                            </p>
                            <span
                              className='flex gap-x-2 font-bold text-xs items-baseline pl-1'
                              style={{
                                background:
                                  'linear-gradient(to bottom, #6199FF, #045CFB)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                              }}
                            >
                              Available SOON
                            </span>
                          </span>
                        </div>
                      </Button>
                    </ShouldRender> */}

            {/* Start */}
            <div className='flex flex-col w-full'>
              <div className='grid grid-cols-2 gap-2.5 w-full'>
                <div className='relative min-w-[151px] flex flex-1 rounded-lg h-full'>
                  {/* Hard Copy Button */}
                  <ShouldRender
                    check={bookLandingDetails?.hardCoverAvailable !== false}
                  >
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddDataToCart(
                          {
                            id: Number(
                              bookLandingDetails?.hardCoverProductId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                          },
                          'hardCopy',
                        );

                        trackAddToCartEvent({
                          productData: {
                            price: Number(
                              bookLandingDetails?.hardCopyPrice
                                ?.discountedPrice,
                            ),
                            id: Number(bookLandingDetails?.hardCoverProductId),
                            heading: bookLandingDetails?.productTitle as string,
                            quantity: 1 as number,
                          },
                          userData,
                          pathname: router.pathname,
                          productType: 'hardcopy',
                        });
                      }}
                      className='active:scale-95 transition-transform cursor-pointer relative min-w-[151px] flex-1 flex flex-col gap-0.5 px-3 py-2.5 bg-white rounded-lg'
                    >
                      <div className='flex items-center justify-between gap-2.5'>
                        <p className='text-xs/5 font-semibold text-black'>
                          Hard Copy
                        </p>
                        <ArrowSmRightIcon className='w-4 h-4 text-gray-700' />
                      </div>
                      <div className='flex items-center gap-1'>
                        <p className='text-base/6 font-semibold text-black'>
                          ₹
                          {
                            slidBookContainerData?.hardCopyPrice
                              ?.discountedPrice
                          }
                        </p>
                        <p className='text-sm/6 font-medium text-grayZl-700 line-through'>
                          {slidBookContainerData?.hardCopyPrice?.mrpPrice}
                        </p>
                      </div>
                      <button className='shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.25)] bg-white z-40 top-[-11px] left-[-8px] rounded-[4px] px-1.5 py-0.5 absolute uppercase text-[10px] leading-[14px] font-semibold border border-primary text-primary'>
                        Free Digital Copy
                      </button>
                    </div>
                  </ShouldRender>

                  {/* Avaiable Soon */}
                  <ShouldRender
                    check={bookLandingDetails?.hardCoverAvailable === false}
                  >
                    <div className='w-full bg-black rounded-lg border-[1.5px] border-grayZl-850 h-full flex flex-col items-center justify-center'>
                      <p className='text-xs/5 font-semibold text-center'>
                        Hard Copy
                      </p>
                      <p className='text-xs/5 font-medium  text-transparent bg-[linear-gradient(180deg,#F9742C_0%,#F20486_100%)] bg-clip-text'>
                        Available Soon
                      </p>
                    </div>
                  </ShouldRender>
                </div>

                {/* Soft Copy Button */}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddDataToCart(
                      {
                        id: Number(
                          bookLandingDetails?.digitalProductId,
                        ) as number,
                        temp_user_id: localUserId,
                        quantity: 1,
                      },
                      'digitalCopy',
                    );

                    trackAddToCartEvent({
                      productData: {
                        price: Number(
                          bookLandingDetails?.softCopyPrice?.discountedPrice,
                        ),
                        id: Number(bookLandingDetails?.digitalProductId),
                        heading: bookLandingDetails?.productTitle as string,
                        quantity: 1 as number,
                      },
                      userData,
                      pathname: router.pathname,
                      productType: 'digitalcopy',
                    });
                  }}
                  className='active:scale-95 transition-transform min-w-[151px] flex-1 flex flex-col gap-0.5 px-3 py-2.5 bg-grayZl-900 rounded-lg'
                >
                  <div className='flex items-center justify-between gap-2.5'>
                    <p className='text-xs/5 font-semibold text-white'>
                      Digital Copy
                    </p>
                    <ArrowSmRightIcon className='w-4 h-4 text-grayZl-700' />
                  </div>
                  <div className='flex items-center gap-1'>
                    <p className='text-base/6 font-semibold text-white'>
                      ₹{slidBookContainerData?.softCopyPrice?.discountedPrice}
                    </p>
                    <p className='text-sm/6 font-medium text-grayZl-700 line-through'>
                      {slidBookContainerData?.softCopyPrice?.mrpPrice}
                    </p>
                  </div>
                </div>
              </div>

              {isSubscriptionActive(userData) ? (
                <div className='mt-2.5'>
                  {userData?.subscription?.snapshot?.rules?.[0]?.physical ? (
                    purchasedData.hardProduct.purchased ? (
                      React.cloneElement(claimHardButton, {
                        onClick: () => {
                          notify('Already Purchased', 'info');
                        },
                        disabled: false,
                        className: clsx(
                          claimHardButton.props.className,
                          'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                        ),
                      })
                    ) : keyCount !== 0 ? (
                      claimHardButton
                    ) : (
                      React.cloneElement(claimHardButton, {
                        onClick: () => {
                          notify('Insufficient Credit', 'info');
                        },
                        disabled: false,
                        className: clsx(
                          claimHardButton.props.className,
                          'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                        ),
                      })
                    )
                  ) : userData?.subscription?.snapshot?.rules?.[0]?.digital ? (
                    purchasedData.softProduct.purchased ? (
                      React.cloneElement(claimSoftButton, {
                        onClick: () => {
                          notify('Already Purchased', 'info');
                        },
                        disabled: false,
                        className: clsx(
                          claimSoftButton.props.className,
                          'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                        ),
                      })
                    ) : keyCount ? (
                      claimSoftButton
                    ) : (
                      React.cloneElement(claimSoftButton, {
                        onClick: () => {
                          notify('Insufficient Credit', 'info');
                        },
                        disabled: false,
                        className: clsx(
                          claimSoftButton.props.className,
                          'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)]',
                        ),
                      })
                    )
                  ) : (
                    <></>
                  )}
                </div>
              ) : null}

              <ShouldRender
                check={
                  !isSubscriptionActive(userData) &&
                  hasPreviouslySubscribed(userData)
                }
              >
                {React.cloneElement(claimHardButton, {
                  onClick: () => {
                    notify('Resubscribe Now', 'info');
                  },
                  disabled: false,
                  className: twMerge(
                    claimHardButton.props.className,
                    'shadow-none opacity-30 bg-[radial-gradient(50%_150%_at_46.7%_0%,#7f7f7f_0%,_#7f7f7f_100%)] mt-2.5',
                  ),
                })}
              </ShouldRender>

              {/* <div className='text-white flex gap-x-2 text-xs/5 font-normal justify-center my-0.5'>
                        <SecurePaymentV2 /> Secure Payments
                      </div> */}
            </div>
            {/* End */}
          </>
        );
    }
  };

  return (
    <>
      <Head>
        <title key='title'>
          {metaData?.title || 'Book Details - ZebraLearn'}
        </title>
        <meta
          name='description'
          content={
            metaData?.description || 'Explore our latest books at ZebraLearn.'
          }
          key='description'
        />
        <meta
          name='keywords'
          content={metaData?.keywords || 'books, learning, education'}
          key='keywords'
        />
        <meta
          name='author'
          content={metaData?.author || 'ZebraLearn'}
          key='author'
        />

        <meta
          property='og:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='og-title'
        />
        <meta
          property='og:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='og-description'
        />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG-image.png'
          }
          key='og-image'
        />

        <meta
          property='og:image:type'
          content='image/png'
          key='og-image-type-png'
        />
        {/* Twitter Metadata (Overrides Global) */}
        <meta
          name='twitter:title'
          content={
            metaData?.openGraph?.title || metaData?.title || 'ZebraLearn'
          }
          key='twitter-title'
        />
        <meta
          name='twitter:description'
          content={
            metaData?.openGraph?.description || metaData?.description || ''
          }
          key='twitter-description'
        />
        <meta
          name='twitter:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/OG+1.png?tr=f-png'
          }
          key='twitter-image'
        />
        <meta property='og:image:width' content='1200' />
        <meta property='og:image:height' content='630' />
        <meta
          property='article:section'
          content='Website Section'
          key='article-section'
        />
        <meta
          property='article:tag'
          content={metaData?.keywords as string}
          key='article-tag'
        />
      </Head>

      <Script
        id='product-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ShouldRender
        check={!loading && bookLandingDetails && swp?.toString() !== 'false'}
      >
        <div className=' relative w-full '>
          <ShouldRender check={bookLandingDetails?.bookType === 'comingSoon'}>
            <div className='w-full fixed top-[55px] lg:top-[60px] left-0 bg-gradient-to-r from-black via-[#1a1a1a] to-[#444] text-white text-sm z-30'>
              <div className='max-w-[1240px] mx-auto flex justify-center items-center py-3 md:py-[18px]'>
                <span className='opacity-80 hidden md:inline-block'>
                  Pre-order offer
                </span>
                <span className='opacity-80'>
                  <span className='hidden md:inline-block px-2'>|</span>
                  Get Hard + Digital Book at{' '}
                  <span className='text-[#FF5B00] font-medium'>₹990</span> only
                </span>
              </div>
            </div>
          </ShouldRender>
          <div
            className={clsx(
              bookLandingDetails?.bookType !== 'comingSoon'
                ? 'mt-14 '
                : 'mt-24',
              ' relative flex h-full ',
            )}
          >
            <div className='w-full pb-20 md:pb-24 lg:pb-5'>
              <Section direction='left'>
                <div>
                  <ShouldRender check={!screens.lg}>
                    <Section>
                      <div className='h-full z-40'>
                        <SlidBookContainer
                          purchasedData={purchasedData}
                          slidBookContainerData={
                            slidBookContainerData as ISlidBook
                          }
                          token={token}
                        />
                      </div>
                    </Section>
                  </ShouldRender>
                  <div className=' bg-bgColor lg:bg-transparent md:py-4 lg:py-[56px] px-4  md:px-10 xl:px-20 relative overflow-hidden'>
                    <div className='absolute customGradient inset-0 hidden md:block   translate-y-2 backdrop-blur-xl -z-10'></div>
                    <div className='absolute inset-0 hidden lg:block  bg-[#000000CC]  -z-20'></div>

                    <div className='absolute inset-0 hidden lg:block  w-full h-full -z-30'>
                      <Image
                        src={heroSectionData?.bookImage as string}
                        alt='Background Image'
                        layout='fill'
                        priority
                        className='rotate-90    transform w-full h-auto absolute -translate-y-10 2xl:-translate-y-24 top-0 left-0'
                      />
                    </div>

                    <ShouldRender check={!loading && heroSectionData}>
                      <div className='z-40 '>
                        <HeroSection
                          authorsData={
                            authorsData as IAuthorInterface | IAuthorInterface[]
                          }
                          heroSectionData={
                            heroSectionData as IHeroSectionLandingPage
                          }
                        />
                      </div>
                    </ShouldRender>
                  </div>

                  <div className='lg:w-1/2 xl:w-2/3 pt-[56px] pb-[20px] md:py-[56px] px-5 md:px-10 lg:pl-10 lg:pr-0 xl:px-20 text-white flex flex-col gap-y-[56px]'>
                    <BookDescription
                      description={
                        bookDescriptionData?.bookDescription as string[]
                      }
                      whatYouWillLearn={
                        bookDescriptionData?.whatYouWillLearn as string[]
                      }
                    />
                    <ShouldRender check={!short}>
                      <ShouldRender
                        check={bookLandingDetails?.bookType !== 'comingSoon'}
                      >
                        <IframeLandingPage
                          data={iframeData as IIframeLandingPage}
                        />
                        <RecommendedAndOverViewSection
                          recommendedAndOverViewSectionData={
                            recommendedAndOverViewSectionData as IRecommendedAndOverViewSection
                          }
                        />
                      </ShouldRender>
                    </ShouldRender>

                    <CurriculumSection
                      data={
                        curriculumLandingPageData as ICurriculumLandingPageData
                      }
                    />

                    <SneakPeekSection
                      data={sneakPeekData?.sneakPeek as string[]}
                    />
                    <AuthorDetailsSection
                      data={
                        authorDetailsData as
                          | IAuthorDetails
                          | IAuthorDetailsList[]
                      }
                      authorsData={authorsData}
                      isMultipleAuthors={
                        heroSectionData?.isMultipleAuthors || false
                      }
                      productId={bookDetails?._id || ''}
                      productName={bookDetails?.bookName || ''}
                    />

                    <ReviewsLandingPage
                      isLoggedIn={!!token}
                      digitalCoverId={
                        bookLandingDetails?.digitalProductId?.toString() as string
                      }
                      hardCoverId={
                        bookLandingDetails?.hardCoverProductId?.toString() as string
                      }
                      isComingSoon={
                        bookLandingDetails?.bookType === 'comingSoon'
                      }
                    />

                    <RecommendedBooksSection
                      category={
                        bookLandingDetails?.categories?.[0]?.name ?? undefined
                      }
                      categoryId={
                        bookLandingDetails?.categories?.[0]?.categoryId
                      }
                      token={token}
                      type={
                        bookLandingDetails?.bookType === 'comingSoon'
                          ? 'comingSoon'
                          : 'book'
                      }
                    />
                  </div>
                </div>
              </Section>
            </div>
            <ShouldRender check={screens?.lg}>
              <AnimatePresence mode='wait'>
                <div className='absolute right-20 2xl:right-[8%] top-[8px]'>
                  <Affix offsetTop={offset}>
                    <Section direction='right'>
                      <div className='slid-book-container transition-all duration-300 ease-in-out'>
                        <SlidBookContainer
                          purchasedData={purchasedData}
                          slidBookContainerData={
                            slidBookContainerData as ISlidBook
                          }
                          token={token}
                        />
                      </div>
                    </Section>
                  </Affix>
                </div>
              </AnimatePresence>
            </ShouldRender>

            <ShouldRender check={!screens.lg}>
              <div
                className={clsx(
                  !screens.lg && 'fixed bottom-0 left-0 z-40',
                  'w-full',
                )}
              >
                <div className='bg-black border-t border-grayZl-850 px-4 xxs:px-4 md:px-10 lg:px-0 pt-5 pb-3 backdrop-blur-sm flex gap-x-2.5 xxs:gap-x-2.5 relative z-40'>
                  {renderCTASection()}
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>
      </ShouldRender>

      <ShouldRender check={swp?.toString() === 'false'}>
        <Section>
          <div id='products' className={` flex items-center w-full `}>
            <ShouldRender check={!loading}>
              <Products productData={bookDetails} token={token} />
            </ShouldRender>
          </div>
        </Section>
      </ShouldRender>

      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  try {
    const { bookId } = query;

    const parts = (bookId as string).split('-');

    const metaData = await getLandingPageSeoSSR(parts[parts.length - 1] || '');
    const landingPageData = await getBookLandingPageDataSSR(bookId as string);
    if (!metaData) {
      throw new Error('Invalid metaData response');
    }
    const formattedMetaData = {
      title: metaData?.title || '',
      description: metaData?.description || '',
      image:
        generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${metaData?._id}`,
        ) || '',
      author: landingPageData?.form?.authorName || '',
      keywords: metaData?.keywords || '',
      openGraph: {
        title: metaData?.openGraph?.title ?? null,
        description: metaData?.openGraph?.description ?? null,
        image: metaData?.openGraph?.image ?? null,
      },
      categories: landingPageData?.form?.categories ?? null,
      slug: landingPageData?.form?.categories?.slug ?? null,
      canonicalUrl: metaData?.canonicalUrl ?? 'https://zebralearn.com/',
      price: landingPageData?.form?.hardCopyPrice?.discountedPrice ?? null,
      reviews: metaData?.reviews ?? [],
      ratingValue: landingPageData?.form?.rating ?? null,
      reviewCount: metaData?.reviews?.length ?? 0,
    };

    return {
      props: {
        metaData: formattedMetaData,
      },
    };
  } catch (error) {
    notify('Error fetching metaData:', 'error');

    return {
      props: {
        metaData: null,
      },
    };
  }
};

export default BookDetailsPage;
